import { Readable } from 'stream'
import { inherits } from 'util'
import client from '../apollo'
import moment from 'moment'
import { AudioCommand } from '../audio/AudioCommand'
import { DataCommand } from '../ws/DataCommand'
import { AudioEvent } from '../audio/AudioEvent'
import { getStreamWorker, sendSpeechConfig } from '../ws/stream'
import {
  LO_STO,
  SESH_STO,
  ROUTES,
  UPDATE_REASON,
  logout,
  play,
  scrollToSection,
  HALF_STORY,
  ERROR,
  setAgentState,
  DISCONNECT_REASON,
  DISCONNECT_TIMEOUT,
  generateSuki64bitID,
  FETCH_POLICY,
  setSubmissionWarningModal,
  SUBMISSION_WARNING,
  setError,
  isAthena,
  isElation,
  setToast,
  getPbSectionCtx,
  setSectionDictation,
  deletePbcSectionInCache,
  addPbcSectionInCache,
  autoPopulateICD10AfterAdd,
  inScheduleView,
  refreshUnfinishedNoteCount,
  setCompBeingCreated,
  startCreateNote,
  updatePbcSectionInCache,
  SUBMIT_PREFERENCE,
  isAmwellVisitScreen,
  isAmwellTheme,
  isEpic,
  APPOINTMENT_TYPES
} from '../lib'
import { recordNoteUpdated } from '../analytics'
import Auth from '../config/okta'
import loSto from '../config/loSto'
import seshSto from '../config/seshSto'
import {
  CLEAR_DISCONNECT,
  GET_AUDIO_STREAM_UI_CONTEXT,
  // GET_COMP_BEING_CREATED,
  GET_COMP_BEING_DELETED,
  GET_DEBUG,
  GET_DICTATION,
  GET_SECTION,
  GET_TOAST,
  GET_AGENT_STATE,
  GET_CREATE_AMWELL_NOTE,
  SET_CREATE_AMWELL_NOTE,
  SET_AGENT_STATE,
  SET_AUDIO_STREAM_UI_CONTEXT,
  // SET_COMP_BEING_CREATED,
  SET_COMP_BEING_DELETED,
  SET_DISCONNECT,
  SET_PING_TIMEOUT,
  SET_SECTION,
  SET_SECTIONS_TO_UPDATE,
  SET_SHOULD_UPDATE_SCHEDULE,
  SET_SUBMISSION_STATUS,
  SET_TOAST,
  SET_TOAST_DISMISS,
  SET_CURRENT_COMPOSITION,
  SET_CURRENT_NOTETYPE_ID,
  GET_SCHEDULE_DATE,
  SET_SOCKET_AUTHENTICATED,
  SET_INSERT_SCRIPT_RES,
  GET_AUDIO_PROCESSED,
  SET_AUDIO_PROCESSED,
  GET_SECTION_BEING_DELETED,
  GET_KEYBOARD_MODE,
  SET_CURSOR_POSITION,
  SET_CURSOR_ACKNOWLEDGED,
  GET_MODAL,
  SET_MODAL,
  SET_MODAL_DISMISS,
  SET_AGENT_POSITION,
  SET_CONTENT_TO_INSERT,
  SET_DICTATION
} from '../apollo/actions'
import { Constants } from '@sukiai/components'
import { halfStory as hs, logger, logIntercomEvent } from '../lib/logging'
import history from '../config/history'
// import { getNewAudioStreamConfiguration } from '../audio'
import get from 'lodash/get'
import find from 'lodash/find'
import jwtDecode from 'jwt-decode'
import {
  GetAppointments,
  GetUnfinishedCompositions,
  GetAllNotes,
  GetPatientList
} from '@sukiai/gql/app'
import { INTERCOM_EVENTS, EMR_DESTINATION } from '../lib/constants'
import { showNoteTypesModal, createComposition } from './ActionStream'
import { GetAllNoteTypes } from '@sukiai/gql/app/NoteTypes'
const { AGENT, SOUNDS, SECTION_STATE, TOAST, MODALS } = Constants

// global class var to keep track if user is dictating in the problem name
let currentlyDictatingProblemName = false

/**
 * Listens to messages coming from the Audio Worker
 */
function RecognitionStream (options) {
  Readable.call(this, options)

  seshSto.session(SESH_STO.SOCKET_AUTHENTICATED, false)

  const self = this
  options.worker.onmessage = async e => {
    const stream = getStreamWorker()

    if (e.data.event === AudioEvent.ACTION) {
      // e.data.payload could hold either an intent or an availability object.
      if (e.data.payload.intent == null) {
        return
      }
      const intentType = get(e, 'data.payload.intent.type', null)
      if (intentType === null) {
        return
      }
      console.info(`${e.data.event} Received from AudioWorker: first ${e.data.payload.intent.type}`)
    }
    if (e.data.event !== AudioEvent.AUDIOLOG) {
      console.info(`${e.data.event} Received from AudioWorker: ${e.data.payload || ''}`)
    }

    const setAudioStreamUIContext = audioStreamUIContext => {
      return client.mutate({
        mutation: SET_AUDIO_STREAM_UI_CONTEXT,
        variables: { audioStreamUIContext }
      })
    }

    const setAgentReady = () => {
      setAgentState(AGENT.STATE.READY)
      setAudioStreamUIContext({ agentAwake: false })
    }

    const handleDismiss = () => {
      sendSpeechConfig('Dismissing modal')
      const { modal: { open } } = client.readQuery({ query: GET_MODAL })
      if (open) {
        client.mutate({ mutation: SET_MODAL_DISMISS })
      }
    }

    let userId = loSto.get(LO_STO.USER_ID)
    const socketId = get(e, 'data.payload.id')
    const { audioStreamUIContext } = client.readQuery({ query: GET_AUDIO_STREAM_UI_CONTEXT })
    switch (e.data.event) {
      case DataCommand.GET_COMPOSITION_OR_NOTE_RESPONSE:
      {
        const response = e.data.payload
        const {
          composition,
          compositionOrNote
        } = response

        if (compositionOrNote === 'composition') {
          loSto.set(LO_STO.CURRENT_COMPOSITION, composition)

          client.mutate({
            mutation: SET_CURRENT_COMPOSITION,
            variables: {
              composition: JSON.stringify(composition)
            }
          })

          composition.sectionsS2.forEach(section => {
            client.mutate({
              mutation: SET_CURSOR_POSITION,
              variables: {
                sectionId: section.id,
                cursorPositionIndex: section.cursorPosition,
                location: 'CONTENT'
              }
            })
          })

          const noteTypeId = composition.metadata?.noteTypeId

          client.mutate({
            mutation: SET_CURRENT_NOTETYPE_ID,
            variables: {
              id: noteTypeId
            }
          })
        }

        break
      }

      case DataCommand.SERVER_SUBMIT_COMPOSITION_RESPONSE:
      {
        const { submitCompositionResponseType, submitCompositionResponse, submitCompositionResponseErrorMessage } = e.data.payload
        const noteId = submitCompositionResponse?.noteId
        const submissionStatus = submitCompositionResponse?.submissionStatus
        const submissionInformation = submitCompositionResponse?.metadata?.submissionInformation

        if (submitCompositionResponseErrorMessage) {
          console.error(`Error submitting note: ${noteId}. Error message: ${submitCompositionResponseErrorMessage}`)
          setTimeout(() => {
            setError(submitCompositionResponseErrorMessage)
          }, 400)
          return
        }

        if (submitCompositionResponseType !== 'SUCCESS') {
          console.error(`Error submitting note: ${noteId}; submissionStatus: ${submissionStatus}`)
          setTimeout(() => {
            setError('Could not submit note. Please try again.')
          }, 400)
          return
        }

        const userId = loSto.get(LO_STO.USER_ID)

        refreshUnfinishedNoteCount()

        client.query({
          query: GetUnfinishedCompositions,
          variables: {
            userId
          }
        })

        client.query({
          query: GetAllNotes,
          variables: {
            userId
          }
        })

        client.query({ query: GET_SCHEDULE_DATE }).then(({ data: { scheduleDate } }) => {
          const { date } = scheduleDate
          client.query({
            query: GetAppointments,
            variables: {
              ownerId: userId,
              startsAtRangeBeginning: moment(date)
                .startOf('day')
                .toISOString(),
              startsAtRangeEnd: moment(date)
                .endOf('day')
                .toISOString()
            }
          })
        })

        const submissionStatusWarning = get(submissionStatus, 'warning.message')

        // TODO: This is a special case handling for the no_encounter warning - but a larger
        // refactoring of submission warning logic is necessary. A state machine would benefit us.
        // If warning is already set in loSto, its already been handled
        const submissionWarningType = get(submissionStatus, 'warning.type')
        const isNoEncounterWarning = submissionWarningType === SUBMISSION_WARNING.NO_ENCOUNTER

        // If we get a Submission Status Warning in the response, set it in loSto
        if (submissionStatusWarning) {
          loSto.set(
            LO_STO.SUBMISSION_WARNING,
            get(submissionStatus, 'warning.type')
          )
          loSto.set(
            LO_STO.SUBMISSION_WARNING_MSG,
            get(submissionStatus, 'warning.message')
          )
        }

        // [DIGI-396]: Handle Precharting Scenarios
        const [primaryDestination, secondaryDestination] = loSto.get(
          LO_STO.SUBMISSION_DESTINATIONS
        )

        if (
          primaryDestination !== EMR_DESTINATION.DOCUMENT &&
          secondaryDestination === EMR_DESTINATION.DOCUMENT &&
          submissionStatusWarning && !loSto.get(LO_STO.SUBMISSION_TRY_ALL_DESTINATION)
        ) {
          // secondary destination is document and primary destination is not a document
          // set tryAllDestination to be true and open PDF Submit dialog
          setSubmissionWarningModal()
          loSto.set(LO_STO.SUBMISSION_SUCCESSFUL, false)
          loSto.set(LO_STO.SUBMISSION_TRY_ALL_DESTINATION, true)
          return
        }

        if (submissionStatusWarning) {
          setSubmissionWarningModal()
          loSto.set(LO_STO.SUBMISSION_SUCCESSFUL, false)
        } else {
          console.info('Submitted Note')
          recordNoteUpdated('USER_IS_DONE')
          loSto.set(LO_STO.SUBMISSION_SUCCESSFUL, true)
          const currentNoteTypeId = loSto.get(LO_STO.CURRENT_NOTETYPE_ID)
          const currentPatientId = loSto.get(LO_STO.CURRENT_PATIENT_ID)
          const currentApptId = loSto.get(LO_STO.CURRENT_APPT_ID)
          logger.info('[S2 ALERT - SENDING COMPOSITION_SUBMISSION_SUCCESSUFL EVENT')

          stream.postMessage({
            cmd: DataCommand.USER_REQUEST,
            userRequestType: 'COMPOSITION_SUBMISSION_SUCCESSUFL',
            userRequestData: {
              // note the difference between lhs, rhs (its important for ui-srv contract)
              compositionId: noteId,
              notetypeId: currentNoteTypeId,
              patientId: currentPatientId,
              appointmentId: currentApptId
            },
            ts: new Date().toISOString()
          })

          if (submissionInformation?.destinations?.[0] === 'DOCUMENT_NEEDS_SIGNOFF') {
            const status = submitCompositionResponse?.metadata?.status
            if (status === 'USER_IS_DONE') {
              setToast(TOAST.TYPE.INFO, 'Note will be ready for sign-off to the EMR when Suki has finished processing it.')
            } else {
              setToast(TOAST.TYPE.INFO, 'Note sent as PDF to patient\'s chart in the EMR', TOAST.DURATION.MEDIUM)
            }
          }

          history.push(ROUTES.SCHEDULE)

          if (loSto.get(LO_STO.SUBMIT_PREFERENCE) === SUBMIT_PREFERENCE.WITH_QA) {
            const withEncounterToastMessage = 'Note will be sent to EMR when Suki has finished processing it.'
            const withoutEncounterToastMessage = 'Note will be sent as PDF to patient\'s chart in the EMR when Suki has finished processing it.'
            const otherToastMessage = 'Note will be ready for sign-off when Suki has finished processing it.'
            let toastMessage = ''
            if (isAthena() || isElation()) {
              if (!isNoEncounterWarning) {
                toastMessage = withEncounterToastMessage
              } else {
                toastMessage = withoutEncounterToastMessage
              }
            } else {
              toastMessage = otherToastMessage
            }
            setToast(TOAST.TYPE.PRIMARY, toastMessage, TOAST.DURATION.LONG)
            loSto.remove(LO_STO.SUBMIT_PREFERENCE)
          }
        }

        break
      }
      case DataCommand.DELETE_COMPOSITION_RESPONSE:
        {
          const currentCompId = loSto.get(LO_STO.CURRENT_COMP_ID)
          const { status } = e.data.payload
          if (status) {
            logger.info(`Deleted note: ${currentCompId}`)
            handleDismiss()
            // refetch unfinished compositions count & compositions/notes list
            const userId = loSto.get(LO_STO.USER_ID)

            refreshUnfinishedNoteCount()

            client.query({
              query: GetUnfinishedCompositions,
              variables: {
                userId
              }
            })

            client.query({
              query: GetAllNotes,
              variables: {
                userId
              }
            })

            history.push(ROUTES.SCHEDULE)
          } else {
            handleDismiss()
            console.error('Error deleting note')
            setTimeout(() => {
              setError('Could not delete note. Please try again.')
            }, 400)
          }
        }
        break

      // TODO: how do we support this use case of having only one tab open at a time?
      // case AudioEvent.ACTIVATE_AGENT:
      // {
      //   // This event is only emitted on the node socketio client aka before streaming gateway
      //   console.info('Activate agent received: %s', e.data.payload)
      //   if (e.data.payload === true) {
      //     // [TODO: Mark] Stuffing in this logic because activate agent is always
      //     // called on refresh. Is important for tracking doctor context during refresh.
      //     if (window.location.pathname.includes('/note')) {
      //       stream && stream.postMessage({
      //         cmd: DataCommand.DOC_ENTERED_NOTE,
      //         noteId: window.location.pathname.split('/')[2],
      //         userId
      //       })
      //       recordNoteEvent(NOTE_EVENT_METRICS.ENTERED, {
      //         noteId: loSto.get(LO_STO.CURRENT_COMP_ID),
      //         organizationId: loSto.get(LO_STO.ORGANIZATION_ID),
      //         tabId: window.sessionStorage.tabID
      //       })
      //     }
      //     break
      //   }
      //   setAgentState(AGENT.STATE.MUTED)
      //   break
      // }
      case AudioEvent.MUTE_AGENT:
        if (e.data.onLogin && isAmwellVisitScreen()) {
          setAgentState(AGENT.STATE.MUTED)
          break
        }
        if (e.data.payload) {
          setAgentState(AGENT.STATE.MUTED)
        } else {
          client.mutate({
            mutation: SET_AGENT_STATE,
            variables: {
              agentState: AGENT.STATE.READY
            }
          })
        }
        break
      case AudioEvent.OFFLINE_TOAST:
        client.mutate({
          mutation: SET_TOAST,
          variables: {
            type: TOAST.TYPE.ERROR,
            message: ERROR.PING_TIMEOUT,
            duration: TOAST.DURATION.INDEFINITE
          }
        })
        break
      case AudioEvent.ONLINE_REMOVE_TOAST:
        client.mutate({ mutation: SET_TOAST_DISMISS })
        break
      case AudioEvent.AUTH_REQUIRED:
      {
        console.info('Providing auth token')
        logger.info('Providing auth token')
        const traceId = generateSuki64bitID()
        const sessionId = loSto.get(LO_STO.SESSION_ID)
        loSto.set(LO_STO.CURRENT_TRACE_ID, traceId)
        // Transfer Okta ID Token to worker for authenticating socket.io connection

        // TODO [morhm], the jwt probably isn't right
        Auth.getIdToken()
          .then(jwt => {
            console.info('Inside promise providing auth token')
            // Set userId
            const user = jwtDecode(jwt)
            userId = user.userID
            loSto.set(LO_STO.USER_ID, userId)
            seshSto.session(SESH_STO.USER_ID, userId)
            loSto.set(LO_STO.ORGANIZATION_ID, user.organizationID)
            const emrType = get(seshSto.session(SESH_STO.CURRENT_ORGANIZATION), 'emr.type')
            const isEmr = !!emrType

            // https://github.com/LearningMotors/web/blob/6e992c899c1f5a774a0d290fc7cd492f6e60cc97/server/lib/graphql/emr/schema.js#L1-L24
            let primaryEmr = 'UNKNOWN'
            let secondaryEmr = 'UNKNOWN_EMR'
            if (isEmr) {
              primaryEmr = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr?.type
              secondaryEmr = seshSto.session(SESH_STO.CURRENT_ORGANIZATION)?.emr?.secondaryType
            }

            const agentMuted = seshSto.session(SESH_STO.AGENT_MUTED)
            const tabId = seshSto.session(SESH_STO.TAB_ID)

            const message = {
              cmd: AudioCommand.AUTH,
              jwt,
              sessionId,
              traceId,
              audioStreamUIContext,
              isEmr,
              userAgent: window.navigator && window.navigator.userAgent,
              primaryEmr,
              secondaryEmr,
              agentMuted,
              tabId
            }

            stream.postMessage(message)
          })
          .catch(err => {
            console.error('Error retrieving id token |', err)
          })
        break
      }
      case AudioEvent.AUTHENTICATED:
      {
        console.info('Authenticated to socket:', socketId)
        logger.info(`Authenticated to socket: ${socketId}`)
        seshSto.session(SESH_STO.SOCKET_AUTHENTICATED, true)

        client.mutate({
          mutation: SET_SOCKET_AUTHENTICATED,
          variables: { isSocketAuthenticated: true }
        })

        // If we attempted to send CREATE_NOTE before socket was authenticated, we now can create the note
        const { data } = await client.query({
          query: GET_CREATE_AMWELL_NOTE
        })

        console.info('Result of Get_CREATE_AMWELL_NOTE', data)

        if (data.createNote) {
          startCreateNote()

          client.mutate({
            mutation: SET_CREATE_AMWELL_NOTE,
            variables: { createNote: false }
          })
        }

        const tabId = seshSto.session(SESH_STO.TAB_ID)
        const activeTab = loSto.get(LO_STO.ACTIVE_TAB)
        const agentMuted = seshSto.session(SESH_STO.AGENT_MUTED)

        // set current tub as active (agent activated) if it is no one setted before and agent is not muted
        if (!activeTab && !agentMuted) {
          loSto.set(LO_STO.ACTIVE_TAB, tabId)
        }

        // [TODO: Mark] Stuffing in this logic because activate agent is always
        // called on refresh. Is important for tracking doctor context during refresh.
        if (window.location.pathname.includes('/note')) {
          stream && stream.postMessage({
            cmd: DataCommand.DOC_ENTERED_NOTE,
            noteId: window.location.pathname.split('/')[2],
            userId
          })
        }
        break
      }
      case AudioEvent.SPEECH_ENDED:
        console.info('Stream ended with id', e.data.payload)
        setAgentState(AGENT.STATE.MUTED)
        break
      case AudioEvent.S2_NOTE_CREATED_IN_NOTEPAD:
      {
        // FOR S2 - Suki Notepad has created a composition ID at this point
        // First, we will check if this current user has more than 1 notetype, if so, we
        // will open notetype dialog. If not, we will send a new socket message called
        // `create_note_with_notetype` to the ui-srv which sends a USER_REQUEST of type
        // `CREATE_NOTE_WITH_NOTETYPE` to S2.
        const { data: { noteTypes } } = await client.query({
          query: GetAllNoteTypes,
          variables: {
            userId: loSto.get(LO_STO.USER_ID)
          }
        })

        if (!noteTypes || noteTypes?.count === 0) {
          client.mutate({
            mutation: SET_AGENT_POSITION,
            variables: {
              agentPosition: AGENT.POSITION.LEFT
            }
          })
          setError('You don\'t have any Note Types.')
          loSto.remove(LO_STO.CURRENT_APPT_ID)
          loSto.remove(LO_STO.LAST_OPENED_APPT)
          setCompBeingCreated(false)

          if (inScheduleView()) {
            loSto.remove(LO_STO.CURRENT_PATIENT_ID)
          }

          stream.postMessage({
            cmd: DataCommand.USER_REQUEST,
            userRequestType: 'CANCEL_CREATE_NOTE',
            userRequestData: {
              compositionId: e.data.payload.composition_id
            },
            ts: new Date().toISOString()
          })
        } else if (noteTypes?.count === 1) {
          loSto.set(LO_STO.CURRENT_COMP_ID, e.data.payload.composition_id)
          createComposition(noteTypes?.results[0]?.id, loSto.get(LO_STO.CURRENT_PATIENT_ID), loSto.get(LO_STO.CURRENT_APPT_ID))
        } else {
          loSto.set(LO_STO.CURRENT_COMP_ID, e.data.payload.composition_id)
          showNoteTypesModal()
        }

        setAgentReady()

        break
      }
      // NOTE_CREATED: redirect the user to the composition and load it from data sent from server
      case AudioEvent.NOTE_CREATED:
      {
        e.data.payload && hs.add(HALF_STORY.NOTE_CREATED, `Created note ${e.data.payload.id}.`)
        e.data.payload && logIntercomEvent(INTERCOM_EVENTS.NOTE_CREATED)
        e.data.payload && loSto.set(LO_STO.CURRENT_COMPOSITION, e.data.payload)

        // also need to set the composition in grapqhql state and load it in the note view
        const composition = {
          ...e.data.payload
        }

        e.data.payload && client.mutate({
          mutation: SET_CURRENT_COMPOSITION,
          variables: {
            composition: JSON.stringify(composition)
          }
        }).then(() => {
          history.push(`${ROUTES.NOTE}/${e.data.payload.id}`)

          refreshUnfinishedNoteCount()
        })

        // so the agent_processing intent ring goes away for voice commands
        setAgentReady()
        break
      }
      case AudioEvent.AUDIO_PROCESSED:
      {
        client.query({
          query: GET_KEYBOARD_MODE
        }).then(({ data: { keyboardMode } }) => {
          if (keyboardMode) {
            client.mutate({
              mutation: SET_AUDIO_STREAM_UI_CONTEXT,
              variables: {
                audioStreamUIContext: {
                  agentAwake: false
                }
              }
            }).then(() => {
              sendSpeechConfig('Sending ui context before keyboard ON is called.')
              stream.postMessage({
                cmd: DataCommand.EVENT,
                eventType: 'KEYBOARD_ON',
                ts: new Date().toISOString()
              })
              logger.info('[S2 ALERT - RECEIVED AUDIO_PROCESSED AFTER CLIENT ENTERED KEYBOARD MODE AND SENT AUDIO_END]')
              logger.info('[S2 ALERT - SENDING KEYBOARD_ON]')
              setAgentState(AGENT.STATE.MUTED)
              logger.info('[S2 ALERT - MUTED_AGENT]')
            })
          } else {
            logger.info('[S2 ALERT - RECEIVED AUDIO_PROCESSED - GENERIC]')
          }
        })

        client.query({
          query: GET_AUDIO_PROCESSED
        }).then(({ data: { isProcessed } }) => {
          if (!isProcessed) {
            client.mutate({
              mutation: SET_AUDIO_PROCESSED,
              variables: {
                isProcessed: true
              }
            })
          }
        })
        break
      }
      case AudioEvent.CURSOR_ACKNOWLEDGED:
      {
        client.mutate({
          mutation: SET_CURSOR_ACKNOWLEDGED,
          variables: {
            isAcknowledged: true
          }
        })
        break
      }
      case DataCommand.SECTION_CONTENT_RESPONSE:
        {
          const { sectionContentById } = e.data.payload

          const sectionContentMap = {}
          sectionContentById.forEach(s => {
            sectionContentMap[s.sectionId] = s.sectionContent
          })

          client.mutate({
            mutation: SET_CONTENT_TO_INSERT,
            variables: {
              sections: JSON.stringify(sectionContentMap)
            }
          })
        }
        break
      case DataCommand.SECTION_RESPONSE:
        {
          const { sections, sectionResponseType } = e.data.payload

          if (
            sectionResponseType === 'UNDO_DELETE_THIS_UPDATE' ||
            sectionResponseType === 'SECTION_FORMATTED'
          ) {
            setAgentReady()
          }

          if (sectionResponseType === 'NO_MATCHING_SECTION_FOUND_FOR_MACRO') {
            setAgentReady()
            setToast(TOAST.TYPE.WARNING, 'Script section not found in note', TOAST.DURATION.MEDIUM)
            client.mutate({
              mutation: SET_INSERT_SCRIPT_RES,
              variables: {
                isPending: false,
                error: sectionResponseType
              }
            })
            return
          }

          if (sectionResponseType === 'NO_SECTION_IN_FOCUS_FOR_ANY_SECTION_MACRO') {
            setAgentReady()
            setToast(TOAST.TYPE.WARNING, 'No section is in focus.', TOAST.DURATION.MEDIUM)
            play(SOUNDS.ERROR)
            client.mutate({
              mutation: SET_INSERT_SCRIPT_RES,
              variables: {
                isPending: false,
                error: sectionResponseType
              }
            })
            return
          }

          if (sectionResponseType === 'SAME_SECTION_NAME_ADDED') {
            setAgentReady()
            play(SOUNDS.ERROR)
            setToast(TOAST.TYPE.WARNING, 'This note already has a problem with that name', TOAST.DURATION.MEDIUM)
            return
          }

          if (sectionResponseType === 'SECTION_WITH_EMPTY_TITLE_ALREADY_EXISTS') {
            setAgentReady()
            // TODO: set error modal
            return
          }

          if (
            sectionResponseType === 'SCRIPT_INSERT' ||
            sectionResponseType === 'COPY_FORWARD_SECTION' ||
            sectionResponseType === 'SCRIPT_CHANGE_SECTION'
          ) {
            setAgentReady()
            // TODO: unfortunately insert is not animated
            sections.forEach(s => {
              client.mutate({
                mutation: SET_CURSOR_POSITION,
                variables: {
                  sectionId: s.id,
                  cursorPositionIndex: s.cursorPosition,
                  location: 'CONTENT'
                }
              })
            })

            if (sectionResponseType === 'SCRIPT_INSERT') {
              handleDismiss()

              await client.mutate({
                mutation: SET_INSERT_SCRIPT_RES,
                variables: {
                  isPending: false,
                  error: ''
                }
              })
            }

            const firstSectionId = sections[0].id

            scrollToSection(firstSectionId)

            sendSpeechConfig(`Change section because of ${sectionResponseType}`)
          }

          if (sectionResponseType === 'ADD') {
            const currentCompId = loSto.get(LO_STO.CURRENT_COMP_ID)
            const sectionInResponse = sections[0]
            sectionInResponse && addPbcSectionInCache(currentCompId, sectionInResponse)
            sectionInResponse && autoPopulateICD10AfterAdd(sectionInResponse)

            client.mutate({
              mutation: SET_AUDIO_STREAM_UI_CONTEXT,
              variables: {
                audioStreamUIContext: {
                  agentAwake: false,
                  problemBasedSections: await getPbSectionCtx(currentCompId)
                }
              }
            }).then(async () => {
              sendSpeechConfig('Sending pbc sections after ADD PBC section was called.')

              const { agentState } = client.readQuery({ query: GET_AGENT_STATE })
              if (agentState !== AGENT.STATE.MUTED) {
                // Also need to put the user in dictation mode in this section
                await setSectionDictation(currentCompId, sectionInResponse.id)
                currentlyDictatingProblemName = true
              }

              // focus in new input element
              const idTag = `section-name-input-${sectionInResponse.id}`
              const inputElement = document.getElementById(idTag)
              inputElement && inputElement.focus()
            })
            return
          }

          if (sectionResponseType === 'DELETE') {
            const currentCompId = loSto.get(LO_STO.CURRENT_COMP_ID)
            const sectionId = await client.query({ query: GET_SECTION_BEING_DELETED }).then((data) => {
              return data?.data?.sectionBeingDeleted?.id
            })
            sectionId && deletePbcSectionInCache(currentCompId, sectionId)
            return
          }

          // if is pbc section && no icd10 code attached
          // then do autopopulate
          const section = sections[0]
          const currentCompId = loSto.get(LO_STO.CURRENT_COMP_ID)

          if (!!section?.diagnosisEntry && section.diagnosisEntry?.diagnosis?.icdCode === '') {
            autoPopulateICD10AfterAdd(section)
          }
          updatePbcSectionInCache(currentCompId, section)

          // Otherwise, just set sections to update
          client.mutate({
            mutation: SET_SECTIONS_TO_UPDATE,
            variables: {
              sections: sections.map(s => {
                return {
                  __typename: 'SectionToUpdate',
                  id: s.id,
                  name: s.name,
                  status: s.status,
                  content: s.content,
                  cursorPosition: s.cursorPosition,
                  cursorPositionName: s.cursorPositionName
                }
              })
            }
          })

          // Just after initial update of name for a PBC section focus on actual editor
          if (currentlyDictatingProblemName && !!section?.diagnosisEntry) {
            const sectionEditorEl = document.getElementById(`sectionEditor-${section.id}`)
            if (sectionEditorEl) {
              sectionEditorEl.focus()
              logger.info(`Focused section ${section.id} after PBC Section Name was dictated [DOM ID=${sectionEditorEl.id}]`)
            }
            currentlyDictatingProblemName = false
          }
        }
        break
      case AudioEvent.NOTE_DELETED:
        hs.add(HALF_STORY.NOTE_DELETED, `Deleted note ${e.data.payload.noteId}`)
        client.query({ query: GET_COMP_BEING_DELETED })
          .then(({ data }) => {
            if (window.location.pathname.includes(e.data.payload.noteId)) {
              if (data.compBeingDeleted) {
                client.mutate({
                  mutation: SET_COMP_BEING_DELETED,
                  variables: { compBeingDeleted: false }
                })
              } else if (!data.compBeingDeleted) {
                history.push(ROUTES.SCHEDULE)
              }
            }
          })

        if (window.location.pathname === ROUTES.SCHEDULE) {
          client.mutate({ mutation: SET_SHOULD_UPDATE_SCHEDULE })
        }
        break

      case AudioEvent.COMPOSITION_UPDATED_S2:
        {
          const { modal } = client.readQuery({
            query: GET_MODAL
          })

          const modalType = modal?.open && modal?.content?.type
          if (modalType === 'EMR_NOTE_PULL') {
            handleDismiss()
            setToast(TOAST.TYPE.PRIMARY, 'Note Refreshed From EMR', TOAST.DURATION.MEDIUM)
          }

          const composition = e.data.payload
          client.mutate({
            mutation: SET_CURRENT_COMPOSITION,
            variables: {
              composition: JSON.stringify(composition)
            }
          })

          const sections = composition?.sectionsS2 || []
          client.mutate({
            mutation: SET_SECTIONS_TO_UPDATE,
            variables: {
              sections: sections.map(s => {
                return {
                  __typename: 'SectionToUpdate',
                  id: s.id,
                  name: s.name,
                  status: s.status,
                  content: s.content,
                  cursorPosition: s.cursorPosition,
                  cursorPositionName: s.cursorPositionName
                }
              })
            }
          })

          const appointmentType = get(composition, 'metadata.appointment.type')
          const patientId = get(composition, 'metadata.patient.id')

          // If patient is on schedule,the note can be attached to their most recent appointment on the Schedule.
          // any appointment type other than default (0) (i.e. typeValue > 0) is already part of schedule
          if (appointmentType !== APPOINTMENT_TYPES.DEFAULT) {
            return
          }

          // Else, if the patient is on the List but not the Schedule, the note can be attached to that
          // since Epic do not have patient list, so return
          if (isEpic()) {
            return
          }

          // check if patient is in patient list
          client.query({
            query: GetPatientList,
            variables: {
              userId: loSto.get(LO_STO.USER_ID),
              organizationId: loSto.get(LO_STO.ORGANIZATION_ID)
            }
          }).then(({ data }) => {
            const patientList = get(data, 'getPatientList.patientList.patients', [])
            const isPatientInList = find(patientList, (patient) => patient.id === patientId)

            if (isPatientInList) {
              return
            }

            //  If the patient is on neither the List nor the Schedule
            //  show popup to add to list or schedule (non-emr only))
            client.mutate({
              mutation: SET_MODAL,
              variables: {
                content: {
                  __typename: 'ModalContent',
                  type: MODALS.ADD_PATIENT_TO_LIST_OR_SCHEDULE,
                  heading: 'Add this patient to your Patients List or Schedule?'
                }
              }
            })
          })

          setAgentReady()
        }
        break
      case AudioEvent.COMPOSITION_UPDATED_S2_FOR_UNDO:
        {
          const composition = e.data.payload?.composition
          const sectionId = e.data.payload?.section_in_focus_id

          client.mutate({
            mutation: SET_CURRENT_COMPOSITION,
            variables: {
              composition: JSON.stringify(composition)
            }
          })

          const sections = composition?.sectionsS2 || []
          client.mutate({
            mutation: SET_SECTIONS_TO_UPDATE,
            variables: {
              sections: sections.map(s => {
                return {
                  __typename: 'SectionToUpdate',
                  id: s.id,
                  name: s.name,
                  status: s.status,
                  content: s.content,
                  cursorPosition: s.cursorPosition,
                  cursorPositionName: s.cursorPositionName
                }
              })
            }
          })

          composition?.sectionsS2?.forEach((section) => {
            client.mutate({
              mutation: SET_CURSOR_POSITION,
              variables: {
                sectionId: section.id,
                cursorPositionIndex: section.cursorPosition,
                location: 'CONTENT'
              }
            })
          })

          scrollToSection(sectionId)

          client.mutate({
            mutation: SET_SECTION,
            variables: { id: sectionId, state: SECTION_STATE.DICTATION }
          })

          setAgentState(AGENT.STATE.LISTENING)

          client.mutate({
            mutation: SET_DICTATION,
            variables: { dictation: true }
          })
        }
        break
      case AudioEvent.COMPOSITION_UPDATED:
      {
        // This is hooked to the `composition_sections_updated` event from the server
        const {
          compositionId: id,
          sections,
          reason
        } = e.data.payload
        const sectionIds = sections && sections.map(s => s.id)
        const currentCompId = window.location.pathname.includes('/note') && window.location.pathname.split('/')[2]
        const firstSectionId = sections[0].id

        // If this update is not for the note currently displayed in the UI, ignore it
        if (id !== currentCompId) {
          logger.info(`Ignoring composition updated message for ID ${id} due to ${reason}`)
          console.debug(`[Editor] Ignoring composition updated message for ID ${id} due to ${reason}`)
          return
        }

        hs.add(HALF_STORY.NOTE_UPDATED, `Sections ${sectionIds} in note ${id} updated due to ${reason}`)
        logger.info(`COMPOSITION UPDATED | Reason: ${reason} | ID: ${id} | Sections: ${sectionIds}`)

        if (id && get(sectionIds, 'length', 0) > 0) {
          const setStu = stu => {
            client.mutate({
              mutation: SET_SECTIONS_TO_UPDATE,
              variables: {
                sections: stu.map(s => ({
                  __typename: 'SectionToUpdate',
                  id: s.id,
                  name: s.name,
                  status: s.status,
                  content: s.content,
                  cursorPosition: s.cursorPosition,
                  cursorPositionName: s.cursorPositionName
                }))
              }
            })
          }

          // Current USER transcription and macro insertions go through this case
          if (reason === UPDATE_REASON.TRANSCRIPTION) {
            logger.info(`[Editor] Setting sections to update: ${sectionIds} in composition: ${id} due to ${reason}`)
            console.debug(`[Editor] Setting STU due to ${reason}`)

            setStu(sections)
          } else if (reason === UPDATE_REASON.MACRO || reason === UPDATE_REASON.COPY_FORWARD) {
            logger.info(`[Editor] Setting sections to update: ${sectionIds} in composition: ${id} due to ${reason}`)
            console.debug(`[Editor] Setting STU due to ${reason}`)
            reason === UPDATE_REASON.COPY_FORWARD && logIntercomEvent(INTERCOM_EVENTS.USED_COPY_FORWARD)
            client.mutate({
              mutation: SET_SECTION,
              variables: {
                id: firstSectionId,
                state: SECTION_STATE.INSERTING
              }
            })

            scrollToSection(firstSectionId)

            setTimeout(() => setStu(sections), 700)
          } else if (reason === UPDATE_REASON.MANUAL_EDIT) {
            // Both current USER edits and SCRIBE edits go through this case
            const { section: { id: currentSectionId } } = client.readQuery({ query: GET_SECTION })
            if (firstSectionId !== currentSectionId) {
              logger.info(`[Editor] Setting sections to update: ${sectionIds} in composition: ${id} due to ${reason}`)
              console.debug(`[Editor] Setting STU due to ${reason}`)

              setStu(sections)
            }

            // To update USER schedule appointment's note status based on scribe edits
            if (window.location.pathname === ROUTES.SCHEDULE) {
              client.mutate({ mutation: SET_SHOULD_UPDATE_SCHEDULE })
            }
          }
        }
        break
      }
      case AudioEvent.COMPOSITION_SUBMISSION_STATUS_UPDATED:
      {
        const { organizationId, compositionId, submissionStatus } = e.data.payload

        // Users from another organization viewing the note in read-only mode should
        // not be booted from the note view. Currently not possible as this message is
        // only emitted to user rooms.
        if (organizationId !== loSto.get(LO_STO.ORGANIZATION_ID)) break

        if (compositionId === loSto.get(LO_STO.CURRENT_COMP_ID)) {
          loSto.set(LO_STO.SUBMISSION_WARNING, get(submissionStatus, 'warning.type'))
          client.mutate({
            mutation: SET_SUBMISSION_STATUS,
            variables: { status: submissionStatus }
          })
        }
        break
      }
      case AudioEvent.COMPOSITION_STATUS_UPDATED:
      {
        const { cid, status } = e.data.payload
        const scheduleDate = loSto.get(LO_STO.SCHEDULE_DATE)
        console.info('[%s] STATUS UPDATED to -> ', cid, status)
        client.query({
          query: GetAppointments,
          variables: {
            ownerId: loSto.get(LO_STO.USER_ID),
            startsAtRangeBeginning: moment(scheduleDate).startOf('day').toISOString(),
            startsAtRangeEnd: moment(scheduleDate).endOf('day').toISOString()
          },
          fetchPolicy: FETCH_POLICY.NETWORK_ONLY
        }).then(({ data }) => {
          client.writeQuery({
            query: GetAppointments,
            data: data
          })
        })
        break
      }
      case AudioEvent.END_AUDIO_ON_ERROR:
      {
        const { errorType, reason } = e.data.payload
        console.error(`RECEIVED A CLOSE STREAM ERROR from S2. Error type: [${errorType}], reason: [${reason}]`)
        logger.error(`RECEIVED A CLOSE STREAM ERROR from S2. Error type: [${errorType}], reason: [${reason}]`)

        // TODO: Surface error modal based on error type?
        // setError('something something')

        // turn off badge?
        // setAgentState(AGENT.STATE.MUTED)

        // For now just reload the page.. should eventually do something more graceful
        window.location.reload()
        break
      }
      case AudioEvent.S2_ERROR:
      {
        // TODO: Fix the root cause of why the "Oops" modal appears in Amwell's Converge environment
        console.error(`Receieved an s2 error: ${e.data.payload.errorMessage}`)
        logger.error(`Received an s2 error recognition stream: ${e.data.payload.errorMessage}`)
        if (isAmwellTheme()) {
          break
        }
        setError(e.data.payload.errorMessage)
      }
      case AudioEvent.CONFIG:
        console.info('Recording Started:', e.data.payload)
        logger.info(`User [${userId}]: Recording Started with config = [${JSON.stringify(e.data.payload)}]`)
        break
      case AudioEvent.AUDIOLOG:
      {
        const logObject = e.data.payload
        const logLevel = logObject && logObject.level
        const logMessage = logObject && logObject.message
        if (logLevel === 'warn') {
          console.warn(logMessage)
          logger.warn(`User [${userId}]: ${JSON.stringify(logMessage)}`)
        } else if (logLevel === 'error') {
          console.error(logMessage)
          logger.error(`User [${userId}]: ${JSON.stringify(logMessage)}`)
        }
        break
      }
      case AudioEvent.PONG_LATENCY:
        logger.info(`[${new Date().toISOString()}] Pong -> Latency: [${e.data.latency} ms]`)
        // If pong latency is > xxxx ms, then we need to take quick action
        break
      case AudioEvent.ERROR:
        logger.warn(`User [${userId}]: Error received: ${e.data.payload} | Details: ${e.data.detail}`)
        console.error('Error Received: msg=%s, details:', e.data.payload, e.data.detail)
        break
      case AudioEvent.INTENT_FAILED:
      {
        logger.warn(`User [${userId}]: ${e.data.intent}_FAILED: ${e.data.message}`)
        console.error(`${e.data.intent}_FAILED: ${e.data.message}`)
        const { dictation } = client.readQuery({ query: GET_DICTATION })

        if (dictation) {
          client.mutate({
            mutation: SET_AUDIO_STREAM_UI_CONTEXT,
            variables: {
              audioStreamUIContext: { agentAwake: false }
            }
          })

          setAgentState(AGENT.STATE.LISTENING)
        } else {
          client.mutate({
            mutation: SET_AUDIO_STREAM_UI_CONTEXT,
            variables: {
              audioStreamUIContext: {
                agentAwake: false,
                transcription: false,
                patientId: ''
              }
            }
          }).then(() => sendSpeechConfig(`Sending speech configuration after ${e.data.intent}_FAILED`))

          setAgentState(AGENT.STATE.READY)
        }

        play(SOUNDS.ERROR)
        client.mutate({
          mutation: SET_TOAST,
          variables: { message: e.data.message }
        })
        break
      }
      case AudioEvent.DISCONNECTED: {
        seshSto.session(SESH_STO.SOCKET_AUTHENTICATED, false)
        const { payload } = e.data
        const { reason, timestamp } = payload

        console.info('Disconnected from socket:', payload)
        logger.warn(`User: ${userId} | Disconnected from socket ${socketId} due to ${reason}`)

        if (reason === DISCONNECT_REASON.PING_TIMEOUT) {
          client.mutate({
            mutation: SET_DISCONNECT,
            variables: { timestamp }
          })
        } else if (reason === DISCONNECT_REASON.IO_SERVER_DISCONNECT) {
          // we sent socket.disconnect(true) from server side
          // try to reconnect
          client.mutate({
            mutation: SET_DISCONNECT,
            variables: { timestamp }
          })
        }

        break
      }
      case AudioEvent.REFUSE_S2_CONNECTION:
        window.alert('Suki is open in another tab. Please close this tab and continue using Suki in the other tab.')
        window.close()
        break
      case AudioEvent.RECONNECTING:
      {
        const numOfAttempts = e.data.payload.attemptNumber
        logger.warn(`User: ${userId} | Socket: ${socketId} | Reconnecting: attempt number ${numOfAttempts}`)
        break
      }
      case AudioEvent.RECONNECT_SUCCESS: {
        logger.info(`User: ${userId} | Socket: ${socketId} | Reconnection successful`)

        logger.info(`User: ${userId} | Socket: ${socketId} | Sending current UI Context`)
        sendSpeechConfig('Client socket reconnected automatically. So, sending current context.')

        const {
          debug: { disconnect }
        } = client.readQuery({ query: GET_DEBUG })

        if (disconnect) {
          const {
            toast: {
              message: toastMessage
            }
          } = client.readQuery({ query: GET_TOAST })

          // If the disconnect toast is up, dismiss it
          if (toastMessage === ERROR.PING_TIMEOUT) {
            client.mutate({ mutation: SET_TOAST_DISMISS })
          }

          const { dictation } = client.readQuery({ query: GET_DICTATION })

          // If the user is in dictation, put them
          // back into the LISTENING agent state.
          // Otherwise, set the agent back to the
          // state that it was in before disconnect.
          if (dictation) {
            client.mutate({
              mutation: SET_AGENT_STATE,
              variables: {
                agentState: AGENT.STATE.LISTENING
              }
            })
          } else {
            const {
              debug: { agentStateBeforeDisconnect }
            } = client.readQuery({ query: GET_DEBUG })

            const wasMuted = agentStateBeforeDisconnect === AGENT.STATE.MUTED

            client.mutate({
              mutation: SET_AGENT_STATE,
              variables: {
                agentState: wasMuted ? AGENT.STATE.MUTED : AGENT.STATE.READY
              }
            })
          }

          // Clear the disconnect timestamp
          client.mutate({ mutation: CLEAR_DISCONNECT })
        }
        break
      }
      case AudioEvent.RECONNECT_ERROR: {
        logger.warn(`User: ${userId} | Socket: ${socketId} | Reconnection failed: ${e.data.payload.error}`)

        const { timestamp } = e.data.payload
        const {
          debug: { disconnect }
        } = client.readQuery({ query: GET_DEBUG })

        if (disconnect && (timestamp - disconnect > DISCONNECT_TIMEOUT)) {
          setAgentState(AGENT.STATE.MUTED)
          client.mutate({
            mutation: SET_TOAST,
            variables: {
              type: TOAST.TYPE.ERROR,
              message: ERROR.PING_TIMEOUT,
              duration: TOAST.DURATION.INDEFINITE
            }
          })
          client.mutate({ mutation: SET_PING_TIMEOUT })
        }
        break
      }
      case AudioEvent.SPEECH_ERROR:
        console.error('Speech Error Received: ', e.data.payload)
        logger.warn(`User [${userId}]: Speech error received [${e.data.payload}]`)
        break
      case DataCommand.TOO_MANY_STREAMS:
        console.warn('Setting TOO_MANY_STREAMS alert')
        logger.warn(`User [${userId}]: Setting TOO_MANY_STREAMS alert`)
        break
      case AudioEvent.UNAUTHORIZED:
        // redirect user to home page
        // TODO [morhm] - Here is why we keep getting redirected. We can't connect via socket.io to s3.
        console.info('Unauthorized - redirecting to home page')
        hs.add(HALF_STORY.LOGOUT, 'Logging out due to unauthorized socket.io connection')
        logout()
        break
      default:
        self.push({
          event: e.data.event,
          payload: e.data.payload,
          timestamp: e.data.timestamp
        })
        break
    }
  }
}

inherits(RecognitionStream, Readable)

RecognitionStream.prototype._read = size => {
  // no-op
}

export default RecognitionStream
